import { importLibrary, setOptions } from '@googlemaps/js-api-loader'

let optionsConfigured = false

function ensureMapsOptions(): void {
  if (optionsConfigured) return
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
  if (!apiKey) {
    throw new Error('VITE_GOOGLE_MAPS_API_KEY is not configured')
  }
  setOptions({ key: apiKey, v: 'weekly' })
  optionsConfigured = true
}

function countryCodeFromGeocoderResult(result: google.maps.GeocoderResult): string {
  const country = result.address_components?.find((component) => component.types.includes('country'))
  return country?.short_name?.toUpperCase() ?? 'AT'
}

export type GeocodeResult = {
  lat: number
  lng: number
  formatted_address: string
  country_code: string
  source: 'google' | 'nominatim'
}

async function geocodeWithGoogle(street: string, city: string, zipcode: string): Promise<GeocodeResult> {
  ensureMapsOptions()
  const { Geocoder } = await importLibrary('geocoding')
  const geocoder = new Geocoder()
  const address = `${street.trim()}, ${zipcode.trim()} ${city.trim()}`

  const response = await geocoder.geocode({ address })
  const result = response.results?.[0]
  const loc = result?.geometry?.location
  if (!loc) {
    throw new Error('Could not validate this address. Check street, postal code, and city.')
  }

  return {
    lat: loc.lat(),
    lng: loc.lng(),
    formatted_address: result.formatted_address ?? address,
    country_code: countryCodeFromGeocoderResult(result),
    source: 'google',
  }
}

type NominatimRow = {
  lat: string
  lon: string
  display_name?: string
  address?: { country_code?: string }
}

async function nominatimQuery(params: URLSearchParams): Promise<NominatimRow | null> {
  params.set('format', 'jsonv2')
  params.set('limit', '1')
  params.set('addressdetails', '1')

  const response = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) {
    throw new Error(`Nominatim responded with ${response.status}`)
  }
  const rows = (await response.json()) as NominatimRow[]
  return rows?.[0] ?? null
}

/** Keyless fallback geocoder (OpenStreetMap Nominatim). */
async function geocodeWithNominatim(street: string, city: string, zipcode: string): Promise<GeocodeResult> {
  // Structured search first; free-form as a secondary attempt.
  let row = await nominatimQuery(
    new URLSearchParams({ street: street.trim(), city: city.trim(), postalcode: zipcode.trim() }),
  )
  if (!row) {
    row = await nominatimQuery(
      new URLSearchParams({ q: `${street.trim()}, ${zipcode.trim()} ${city.trim()}` }),
    )
  }

  const lat = row ? parseFloat(row.lat) : NaN
  const lng = row ? parseFloat(row.lon) : NaN
  if (!row || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    throw new Error('Could not validate this address. Check street, postal code, and city.')
  }

  return {
    lat,
    lng,
    formatted_address: row.display_name ?? `${street.trim()}, ${zipcode.trim()} ${city.trim()}`,
    country_code: row.address?.country_code?.toUpperCase() ?? 'AT',
    source: 'nominatim',
  }
}

/**
 * Geocodes an address with a resilient provider chain:
 * Google Geocoder -> OpenStreetMap Nominatim. Throws only when every
 * provider fails or none can resolve the address.
 */
export async function geocodeAddress(street: string, city: string, zipcode: string): Promise<GeocodeResult> {
  let googleError: unknown
  try {
    return await geocodeWithGoogle(street, city, zipcode)
  } catch (err) {
    googleError = err
    console.warn('Google geocoding unavailable, falling back to Nominatim:', err)
  }

  try {
    return await geocodeWithNominatim(street, city, zipcode)
  } catch (err) {
    console.warn('Nominatim geocoding failed:', err)
    // A reachable provider that found nothing means the address itself is the
    // problem — surface that as a plain validation error.
    if (err instanceof Error && err.message.startsWith('Could not validate')) {
      throw err
    }
    throw new GeocodeUnavailableError('Address lookup is temporarily unavailable.', {
      google: googleError,
      nominatim: err,
    })
  }
}

/** Raised when every geocoding provider failed (offline, key issues, etc). */
export class GeocodeUnavailableError extends Error {
  causes: { google: unknown; nominatim: unknown }

  constructor(message: string, causes: { google: unknown; nominatim: unknown }) {
    super(message)
    this.name = 'GeocodeUnavailableError'
    this.causes = causes
  }
}

export async function createPlacesService(): Promise<google.maps.places.PlacesService> {
  ensureMapsOptions()
  const { Map } = await importLibrary('maps')
  const { PlacesService } = await importLibrary('places')
  const container = document.createElement('div')
  const map = new Map(container)
  return new PlacesService(map)
}
